import { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  CHART_PALETTE,
  fetchRevenue,
  formatCents,
} from "../../lib/reports";
import { downloadCsv, reportFilename } from "../../lib/csv";
import ExportCsvButton from "./ExportCsvButton";

function SectionShell({ title, subtitle, right, children, testId }) {
  return (
    <section
      data-testid={testId}
      className="rounded-md border border-slate-200 bg-white shadow-card"
    >
      <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
        <div>
          <div className="font-heading text-sm font-semibold text-slate-900">
            {title}
          </div>
          {subtitle && (
            <div className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-0.5">
              {subtitle}
            </div>
          )}
        </div>
        {right}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

function NoData({ label = "No data for this period" }) {
  return (
    <div className="flex h-48 items-center justify-center rounded border border-dashed border-slate-200 text-sm text-slate-500">
      {label}
    </div>
  );
}

function Skeleton() {
  return (
    <div className="h-48 animate-pulse rounded bg-slate-100" />
  );
}

export default function RevenueTab({ params }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [groupBy, setGroupBy] = useState("day");

  const load = () => {
    setLoading(true);
    setErr(null);
    fetchRevenue({ ...params, group_by: groupBy })
      .then((d) => {
        setData(d);
        setLoading(false);
      })
      .catch((e) => {
        setErr(e?.response?.data?.detail || "Failed to load revenue");
        setLoading(false);
      });
  };

  useEffect(load, [params.date_from, params.date_to, groupBy]);

  if (loading) {
    return (
      <div className="grid gap-4 lg:grid-cols-2">
        <Skeleton />
        <Skeleton />
        <Skeleton />
        <Skeleton />
      </div>
    );
  }
  if (err) {
    return (
      <div
        data-testid="revenue-error"
        className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700"
      >
        {err}{" "}
        <button
          onClick={load}
          className="ml-3 underline"
          data-testid="revenue-retry"
        >
          Retry
        </button>
      </div>
    );
  }
  if (!data) return null;

  const trendData = (data.mrr_trend || []).map((p) => ({
    date: p.date,
    dollars: p.amount_cents / 100,
  }));

  const exportCsv = () => {
    const rows = [];
    rows.push(["Summary", "MRR (cents)", data.mrr_cents, ""]);
    rows.push(["Summary", "Collections in range (cents)", data.payment_collections_cents, ""]);
    rows.push(["Summary", "Active subscriptions", data.active_subscriptions, ""]);
    rows.push(["Summary", "New subscriptions in range", data.new_subscriptions_in_range, ""]);
    rows.push(["Summary", "Cancelled in range", data.cancelled_subscriptions_in_range, ""]);
    (data.mrr_trend || []).forEach((p) => {
      rows.push(["Trend", p.date, p.amount_cents, ""]);
    });
    (data.collections_by_type || []).forEach((r) => {
      rows.push(["CollectionsByType", r.payment_type, r.count, r.total_cents]);
    });
    (data.subscriptions_by_plan || []).forEach((r) => {
      rows.push(["SubscriptionsByPlan", r.plan_code, r.count, r.amount_cents]);
    });
    downloadCsv(
      reportFilename("revenue", params),
      ["Section", "Key", "Count or value", "Cents"],
      rows
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <ExportCsvButton onExport={exportCsv} testId="export-csv-revenue" />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
      <SectionShell
        title="Collections trend"
        subtitle="payments collected over time"
        testId="revenue-trend"
        right={
          <div className="inline-flex rounded-md border border-slate-200 p-0.5">
            {["day", "week", "month"].map((k) => (
              <button
                key={k}
                onClick={() => setGroupBy(k)}
                data-testid={`revenue-groupby-${k}`}
                className={`px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide rounded ${
                  groupBy === k
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                {k}
              </button>
            ))}
          </div>
        }
      >
        {trendData.length === 0 ? (
          <NoData />
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={trendData} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v.toLocaleString()}`} />
              <Tooltip
                formatter={(v) => [`$${v.toLocaleString()}`, "Collected"]}
              />
              <Line
                type="monotone"
                dataKey="dollars"
                stroke="#f59e0b"
                strokeWidth={2}
                dot={{ r: 3, fill: "#f59e0b" }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </SectionShell>

      <SectionShell
        title="Subscriptions by plan"
        subtitle="active enrollments breakdown"
        testId="revenue-by-plan"
      >
        {(data.subscriptions_by_plan || []).length === 0 ? (
          <NoData />
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={data.subscriptions_by_plan}
                dataKey="count"
                nameKey="plan_code"
                outerRadius={80}
                innerRadius={45}
                paddingAngle={2}
              >
                {data.subscriptions_by_plan.map((_, i) => (
                  <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(v, name, item) => [
                  `${v} subs · ${formatCents(item.payload.amount_cents)}`,
                  item.payload.plan_code,
                ]}
              />
            </PieChart>
          </ResponsiveContainer>
        )}
      </SectionShell>

      <SectionShell
        title="Collections by type"
        subtitle="payment requests collected"
        testId="revenue-by-type"
      >
        {(data.collections_by_type || []).length === 0 ? (
          <NoData />
        ) : (
          <div className="grid grid-cols-3 gap-3">
            {data.collections_by_type.map((row) => (
              <div
                key={row.payment_type}
                data-testid={`revenue-type-${row.payment_type}`}
                className="rounded border border-slate-200 p-3"
              >
                <div className="font-data text-[10px] uppercase tracking-wide text-slate-500">
                  {row.payment_type}
                </div>
                <div className="font-heading text-lg font-bold text-slate-900 mt-1 tabular-nums">
                  {formatCents(row.total_cents)}
                </div>
                <div className="text-[11px] text-slate-500">
                  {row.count} {row.count === 1 ? "payment" : "payments"}
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionShell>

      <SectionShell
        title="Subscription movement"
        subtitle="in selected period"
        testId="revenue-movement"
      >
        <div className="grid grid-cols-2 gap-4">
          <div
            data-testid="revenue-new-subs"
            className="rounded border border-emerald-200 bg-emerald-50/50 p-4"
          >
            <div className="font-data text-[10px] uppercase tracking-wide text-emerald-700">
              New subscriptions
            </div>
            <div className="font-heading text-3xl font-bold text-emerald-700 tabular-nums mt-1">
              {data.new_subscriptions_in_range}
            </div>
          </div>
          <div
            data-testid="revenue-cancelled-subs"
            className="rounded border border-red-200 bg-red-50/50 p-4"
          >
            <div className="font-data text-[10px] uppercase tracking-wide text-red-700">
              Cancelled
            </div>
            <div className="font-heading text-3xl font-bold text-red-700 tabular-nums mt-1">
              {data.cancelled_subscriptions_in_range}
            </div>
          </div>
        </div>
      </SectionShell>
      </div>
    </div>
  );
}
